import {ErrorHandler, NgModule} from '@angular/core';
import { RecaptchaModule, RecaptchaFormsModule, RECAPTCHA_SETTINGS, RecaptchaSettings } from 'ng-recaptcha';
import { BrowserModule } from '@angular/platform-browser';
import { HttpClientModule, HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { AppComponent } from './app.component';
import { routing } from './app.routing';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { ColorPickerModule } from 'ngx-color-picker';
import { AuthGuard, AnonymousGuard, CanDeactivateGuard, CampaignGuard } from './_guards';
import { JwtInterceptor, SentryErrorHandler, TinymceConfig } from './_helpers';
import {
  AuthenticationService,
  UserService,
  RegionService,
  AddressService,
  PhoneService,
  PaymentSourceService,
  CRMService,
  CampaignCategoryService,
  CampaignService,
  ProductCategoryService,
  ProductService,
  FunnelService,
  FunnelStepService,
  FunnelInputService,
  StyleService,
  FunnelActionService,
  AudioFileService,
  EmailTemplateService,
  AutoResponderService,
  CampaignProductService,
  CRMCampaignService,
  StorageService,
  ColorSwatchService,
  EditorColorService,
  FaqService,
  FaqTopicService,
  OrderService,
  IconFileService,
  ClipboardService,
  ProductInfoSectionService,
  SessionService,
  AccountService,
  FunnelStepGroupService,
  CampaignTrackingService,
  CampaignDomainService,
  WidgetService,
  SalesPhraseService,
  CanDeactivateService,
  CallCenterActionService,
  GuiService,
  CallLogsService,
  SmsLogsService,
  MailLogsService,
  ReportsConfigService,
  ReportsService,
  PlanService,
  ResolvePlanService,
  InvoiceService,
  InvoiceItemService,
  InvoiceTransactionService,
  DailyInvoiceService,
  WidgetCategoryService,
  CustomerService,
  SipCredentialService,
  VariantService,
  FaqReportsService,
  CycleService,
  HeartbeatService,
  PerformanceReportService,
  PersonalInfoService,
  PathPerformanceReportService,
  NotificationService,
  RefundReportService,
  ChatbotReportService,
  GatewayTransactionReportService,
  GatewayPerformanceReportService,
  CustomerSupportReportService,
  CustomerJourneyReportService,
  ResolveReportService,
  GatewayDashboardService,
  OrderOptionsReportService,
  SingleStepReportService,
  ExchangeableProductsSetService,
  RelatedProductSetCategoryService,
  RelatedProductSetService,
  FulfillmentService,
  SessionSummaryReportService,
  ApiKeyService,
  ProxyKeyService,
  FolderService,
  PusherService,
  ChargebackReportService,
  MerchantService,
  MarketplaceReportService,
  RetentionReportService,
  OrderRetentionReportService,
  SubscriptionRetentionReportService,
  ReturnRetentionReportService,
  PhoneCallService,
  ShippingSystemService,
  PaymentUpdaterReportService,
  DevEnvService,
  TicketSystemService,
  EmailIntegrationService,
  CheckoutBankService,
  CheckoutGatewayService,
  CheckoutProcessorService,
  MerchantCategoryService,
  MerchantProfileService,
  ProcessorDocumentService,
  SideNavService,
  CancelReasonCategoryService,
  AudienceService,
  PathService,
  SingleStepFilterService,
  CheckoutTemplatesService,
  DeploymentLinkService,
  GmailService,
  MouseflowService,
  CheckoutsService,
  IntercomService,
  AskSolvpathService,
  ApprovalAnalyticsDashboardService,
  ResolveSettingsService,
  ResolveMerchantService,
} from './_services';
import { SessionResultsComponent } from './session-results';
import { LoginComponent } from './login';
import { RegisterSuccessComponent } from './register-success';
import { RegisterComponent, RegisterVerifyComponent } from './register';
import { VisitorsComponent } from './visitors';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { SubscriptionComponent, SubscriptionEditComponent } from './subscription';
import { NgxStripeModule } from 'ngx-stripe';
import { environment } from '../environments/environment';
import { PaymentSourceComponent, CreditCardEditComponent, BankAccountNewComponent,
  CreditCardNewComponent, CreditCardNewFieldsComponent } from './payment-source';
import { CreditCardDirectivesModule } from 'angular-cc-library';
import { AddressComponent, AddressEditComponent, AddressNewComponent, AddressFieldsComponent } from './address';
import { PhoneComponent, PhoneSearchComponent, PhoneNewComponent, PhoneEditComponent } from './phone';
import {
  CRMComponent, CRMEditComponent, CRMNewComponent, CRMTrackingComponent, CRMCopyOrderComponent,
  CrmChargebackConfigComponent
} from './crm';
import { CampaignCategoryComponent, CampaignCategoryFormComponent, ProductCategoryComponent,
  ProductCategoryFormComponent, WidgetCategoryComponent, WidgetCategoryFormComponent } from './category';
import {
  CampaignComponent,
  CampaignNewComponent,
  CampaignEditComponent,
  CampaignPhoneComponent,
  CampaignEmailComponent,
  CampaignVerifyComponent,
  CampaignFunnelsComponent,
  CampaignStylesComponent,
  CampaignMenuComponent,
  CampaignTestComponent,
  CampaignProductsComponent,
  CampaignLinksComponent,
  CampaignMarketplaceComponent,
  CampaignCallCenterComponent,
  CampaignCampaignsComponent, CampaignSummaryComponent, CampaignNewPopupComponent
} from './campaign';
import { HeaderComponent } from './header';
import { AskSolvpathComponent } from './ask-solvpath';
import { TouchEventModule } from 'ng2-events/lib/touch';
import { OutsideEventModule } from "ng2-events/lib/outside";
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { NgxGraphModule } from './ngx-graph';
import { FunnelComponent, FunnelEditComponent, FunnelNewComponent, FunnelStepsComponent, FunnelTreeActionsComponent,
  FunnelPreviewFormComponent, VisualFunnelComponent, ProductFunnelComponent } from './funnel';
import { FunnelStepEditComponent, FunnelStepNewComponent, FunnelStepPreviewFormComponent } from './funnel-step';
import { FunnelInputEditComponent, FunnelInputNewComponent } from './funnel-input';
import { FunnelActionEditComponent, FunnelActionNewComponent } from './funnel-action';
import {
  StyleComponent,
  StyleNewComponent,
  StyleEditComponent,
  StyleUploadComponent,
  StylePreviewFormComponent
} from './style';
import { EditorModule } from '@tinymce/tinymce-angular';
import { AudioFileComponent, AudioFileUploadComponent } from './audio-file';
import { EmailTemplateComponent, EmailTemplateNewComponent, EmailTemplateEditComponent } from './email-template';
import {
  AutoResponderComponent,
  AutoResponderNewComponent,
  AutoResponderEditComponent,
  AutoresponderTriggersComponent,
} from './autoresponder';
import { SidebarComponent } from './sidebar';
import { LegacySidebarComponent } from './legacy-sidebar';
import {
  PasswordChangeComponent, PasswordResetComponent, PasswordResetSuccessComponent,
  PasswordResetConfirmComponent, ResendActivationEmailComponent
} from './password-reset';
import {
  FaqsComponent,
  FaqNewComponent,
  FaqEditComponent,
  FaqTopicNewComponent,
  FaqTopicEditComponent,
} from './faq';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';
import { CollapsibleModule } from 'angular2-collapsible';
import { ChartsModule, ThemeService } from 'ng2-charts';
import { MonacoEditorModule } from 'ngx-monaco-editor';
import { TroubleshooterComponent } from './troubleshooter';
import { SessionComponent, SessionDetailComponent, ChatbotSessionDetailComponent } from './session';
import { SurveyComponent } from './survey';
import { LifelineComponent } from './lifeline';
import {AccountComponent, AccountEmailBlacklistComponent, AccountFormComponent,
  AccountSearchKeywordsComponent} from './account';
import { FunnelStepGroupComponent, FunnelStepGroupFormComponent } from './funnel-step-group';
import { CampaignTrackingComponent, CampaignTrackingFormComponent } from './campaign-tracking';
import { VoiceFunnelComponent } from './voice-funnel';
import { BreadcrumbModule } from "angular-crumbs";
import { CRMCampaignComponent } from './crm-campaign';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import {
  ReportTableComponent,
  ReportsComponent,
  ReportsConfigComponent,
  FaqReportsComponent,
  PerformanceReportComponent,
  PathPerformanceReportComponent,
  RefundReportComponent,
  SessionSummaryReportComponent,
  ChargebackReportComponent,
  MarketplaceReportComponent,
  RetentionReportComponent,
  PaymentUpdaterReportComponent
} from './legacy-reports';
import { CallCenterActionComponent, CallCenterActionFormComponent } from './call-center-action';
import { HybridFunnelComponent } from './hybrid-funnel';
import { HybridCampaignComponent, HybridCampaignNewComponent } from './hybrid-campaign';
import { CampaignDomainComponent, CampaignDomainFormComponent, CampaignDomainPhoneComponent,
  CampaignDomainFunnelsComponent} from './campaign-domain';
import {
  PhoneLogsContainerComponent, SmsLogsContainerComponent, PhoneLogsComponent, SmsLogsComponent, EmailLogsComponent,
  EmailLogsContainerComponent
} from './logs';
import { PreviewFormComponent } from './preview';
import { WebBuilderComponent } from './web-builder';
import { DesignTemplateComponent, TemplateGridComponent, TemplateSettingComponent,
  DesignTemplateEditFormComponent} from './design-template';
import { NgSelectModule } from '@ng-select/ng-select';
import * as Sentry from "@sentry/browser";
import {
  PlanComponent,
  PlanNewComponent,
  PlanEditComponent,
  ResolvePlanComponent,
  ResolvePlanNewComponent,
  ResolvePlanEditComponent,
} from './plan';
import { InvoiceItemComponent, InvoiceItemFormComponent, InvoiceItemsChargeComponent } from './invoice-item';
import { InvoiceComponent, InvoiceDetailComponent, InvoiceChargeComponent, InvoiceSplitComponent,
  InvoiceTransferCreditComponent, InvoiceBillingDateComponent } from './invoice';
import { CustomerComponent } from './customer';
import { TermsAndConditionsComponent } from './terms-and-conditions/terms-and-conditions.component';
import { SipCredentialFormComponent } from "./sip";
import { UserComponent, UserInviteComponent, UserInviteAcceptComponent, UserApiKeyComponent,
  UserProxyKeyComponent} from './user';
import { ProfilerEnableComponent, ProfilerDisableComponent } from './profiler';
import { MatTreeModule } from '@angular/material/tree';
import { MatExpansionModule } from '@angular/material/expansion';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { PersonalInfoComponent } from "./personal-info";
import { NotificationComponent } from './notification/notification.component';
import {
  RelatedProductSetCategoryComponent,
  RelatedProductSetCategoryEditComponent,
  RelatedProductSetCategoryNewComponent
} from './related-product-set-category';
import {RelatedProductSetEditComponent, RelatedProductSetNewComponent} from './related-product-set';
import {
  ReasonCategoryComponent,
  ReasonCategoryEditComponent,
  ReasonCategoryNewComponent
} from './reason-category';
import {FolderComponent, FolderEditComponent, FolderNewComponent} from './folder';
import { MerchantComponent } from './merchant';
import {
  MerchantProfilesComponent,
  MerchantProfilesNewComponent,
  MerchantProfilesEditComponent,
  MerchantProfilesResolveComponent,
  ResolveTestOrderComponent,
} from './merchant-profiles';
import {
  DashboardComponent,
  DashboardFilterBarComponent,
  DashboardOrdersComponent,
  DashboardPhoneComponent,
  DashboardResolveComponent,
  DashboardReturnsComponent,
  DashboardSubscriptionComponent,
  MarketplaceComponent,
  OverviewComponent,
  RetentionComponent,
} from './dashboard';
import { ApprovalAnalyticsComponent, GatewayDashboardComponent, GatewaySummaryComponent } from './dashboard/gateway-dashboard';
import { GatewayFilterBarComponent } from './dashboard/gateway-filter-bar';
import {
  AiInsightsCardComponent,
  BarChartComponent,
  BillingDualCardComponent,
  CardLoaderComponent,
  CardSelectComponent,
  DataTableComponent,
  DestinationPanelComponent,
  DoughnutChartComponent,
  DoughnutLegendTableComponent,
  DoughnutSummaryCardComponent,
  EmptyStateCardComponent,
  InfoCardComponent,
  LineGraphComponent,
  PerfTableCardComponent,
  PerfTableComponent,
  PortalActionsCardComponent,
  ProductActivityCardComponent,
  ProgressChartComponent,
  SplitBarComponent,
  StatRowCardComponent,
  SummarySectionCardComponent,
  TablePanelComponent,
  Unlock360BannerComponent,
  ViewToggleComponent,
} from './dashboard/data-visualization';
import {
  DashboardMarketplaceReportComponent,
  DashboardOrdersReportComponent,
  DashboardOverviewReportComponent,
  DashboardResolveReportComponent,
  DashboardRetentionReportComponent,
  DashboardReturnsReportComponent,
  DashboardSubscriptionReportComponent,
} from './dashboard/reports';
import { LoaderComponent } from './loader/loader.component';
import { CustomMultiSelectComponent } from './custom-multi-select/custom-multi-select.component';
import { DevEnvNewComponent, DevEnvComponent } from './dev-env';
import { SolvpathDocumentationComponent } from './solvpath-documentation';
import { SSOCompleteComponent } from './sso-complete/sso-complete.component';
import { TicketSystemComponent, TicketSystemEditComponent, TicketSystemNewComponent } from './ticket-system';
import { StepsLibraryModule } from './steps-library/steps-library.module';
import { SharedModule } from './_shared/shared.module';
import {AudienceComponent, AudienceEditComponent, AudienceNewComponent} from './audience';
import { PathComponent, PathEditComponent, PathNewComponent} from './path-builder';
import { PathStepComponent } from './path-step';
import { PathCrudPopupComponent } from './path-builder/path-crud-popup';
import { AudiencePopupComponent } from './audience/audience-popup';
import { NgxSmartModalModule } from 'ngx-smart-modal';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DndModule } from '@beyerleinf/ngx-dnd';
import { AngularDualListBoxModule } from 'angular-dual-listbox';
import { ImageCropperModule } from 'ngx-image-cropper';
import { NgxUploaderModule } from 'ngx-uploader';
import { CustomerPortalEditComponent } from './customer-portal';
import { ChargebackReportV2Component, NewOrderRetentionReportV2Component, OrderRetentionReportV3Component,
  ReturnRetentionReportV3Component, PaymentUpdaterReportV2Component,
  RefundReportV2Component, ReportOverviewPanelV3Component, ReportTopIntentsPanelV3Component,
  ReportPortalMomentsPanelV3Component, ReportPortalActionsPanelV3Component, ReportTableV2Component,
  ReturnRetentionReportV2Component,
  SPTouchedChargebackReportV2Component, SubscriptionRetentionReportV2Component, SubscriptionRetentionReportV3Component,
  ChatbotReportV2Component,
  GatewayTransactionReportV2Component,
  GatewayPerformanceReportV3Component,
  OrderOptionsReportComponent,
  TrafficSourceReportComponent,
  CustomerSupportReportV3Component,
  CustomerJourneyReportV3Component,
  ResolveReportV3Component,
  SingleStepReportComponent} from './reports';
import { ShippingSystemComponent, ShippingSystemNewComponent, ShippingSystemEditComponent,
  ShippingSystemLabelFieldsComponent} from './shipping-system';
import { InvoiceTransactionComponent, InvoiceTransactionRefundComponent,
  InvoiceTransactionEditComponent } from './invoice-transaction';
import {
  EmailIntegrationForwardingEditComponent,
  EmailIntegrationForwardingNewComponent,
  EmailIntegrationSelectComponent, GmailAuthCallbackComponent
} from './email-integration';
import { CheckoutsModule } from './checkouts/checkouts.module';
import {ChatbotQuicklinkComponent} from './chatbot-quicklink';
import {ChatbotInactivityMessageComponent} from './chatbot-inactivity-message';
import {NgxQRCodeModule} from '@techiediaries/ngx-qrcode';
import {MouseflowToggleComponent} from './mouseflow';
import {
  CampaignBrandingComponent,
  CampaignGeneralComponent,
  CampaignSidebarComponent,
  CampaignGeneralEditPopupComponent,
  CampaignDomainsComponent,
  CampaignCallCenterSettingsComponent,
  PhoneContactFieldsComponent,
  CampaignPhoneSetupComponent,
  CampaignEmailSetupComponent,
  PhoneSetupPopupComponent,
  CampaignAutorespondersComponent,
  ContactFieldPopupComponent,
  AutoresponderComponent,
  AutoResponderResponseComponent,
  EmailTemplatePopupComponent,
  CampaignCustomerLoginSettingsComponent,
  CampaignPolicyComponent,
  CampaignDeploymentPageComponent,
  CampaignCustomerPortalSettingsComponent,
  CampaignMarketplaceSettingsComponent,
  CampaignCRMsComponent,
} from './campaign-v2';
import {OfferBuilderModule} from './offer-builder/offer-builder.module';
import {ResolveSettingsModule} from './resolve-settings/resolve-settings.module';
import { SmartlinkPreviewComponent, SmartlinkPreviewFormComponent } from './smartlink-preview';
import {
  OrderManagementPortalComponent,
  OmpHeaderComponent,
  OmpSidebarComponent,
  OmpColorInputComponent,
  OmpLabelInputComponent,
  OmpToggleComponent,
  OmpSliderComponent,
  OmpFaqTreeComponent,
  OmpRteComponent,
  OmpImageFieldComponent,
  OmpPickerService,
  OmpWelcomeMessageSettingsComponent,
  OmpAnnouncementBarSettingsComponent,
  OmpTextBoxSettingsComponent,
  OmpSubscriptionListSettingsComponent,
  OmpOrderListSettingsComponent,
  OmpOrderSummarySettingsComponent,
  OmpShippingPaymentSettingsComponent,
  OmpNextOrderDetailsSettingsComponent,
  OmpMomentCardSettingsComponent,
  OmpIconGlyphComponent,
  OmpIconFieldComponent,
  OmpIconPickerComponent,
  OmpCrossSellCarouselSettingsComponent,
  OmpImageBannerSettingsComponent,
  OmpFaqSettingsComponent,
  OmpFooterSettingsComponent,
  OmpInstagramSettingsComponent,
  OmpNavBlocksSettingsComponent,
  OmpCtaFieldsComponent,
  OmpTextAndImageSettingsComponent,
  OmpCalloutSettingsComponent,
  OmpVideoSettingsComponent,
  OmpColumnsSettingsComponent,
} from './order-management-portal';

if (environment.sentryDsn) {
  Sentry.init({
    dsn: environment.sentryDsn,
    debug: environment.sentryDebugMode
  });
}

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

@NgModule({
  imports: [
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    NgxDatatableModule,
    NgSelectModule,
    RecaptchaModule.forRoot(),
    RecaptchaFormsModule,
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    NgbModule,
    CreditCardDirectivesModule,
    NgxStripeModule.forRoot(environment.stripePublicKey),
    TouchEventModule,
    OutsideEventModule,
    DragDropModule,
    NgxChartsModule,
    NgxGraphModule,
    BrowserAnimationsModule,
    EditorModule,
    NgxMaterialTimepickerModule,
    NgMultiSelectDropDownModule.forRoot(),
    CollapsibleModule,
    ChartsModule,
    MonacoEditorModule.forRoot(),
    NgxSmartModalModule.forRoot(),
    DndModule,
    AngularDualListBoxModule,
    ImageCropperModule,
    NgxUploaderModule,
    BreadcrumbModule,
    NgxDaterangepickerMd.forRoot(),
    ColorPickerModule,
    routing,
    MatTreeModule,
    MatExpansionModule,
    StepsLibraryModule,
    OfferBuilderModule,
    ResolveSettingsModule,
    CheckoutsModule,
    SharedModule,
    NgxQRCodeModule
  ],
  exports: [
    TouchEventModule,
    OutsideEventModule,
  ],
  declarations: [
    AppComponent,
    VisitorsComponent,
    LoginComponent,
    RegisterComponent,
    RegisterVerifyComponent,
    SubscriptionComponent,
    SubscriptionEditComponent,
    PaymentSourceComponent,
    CreditCardEditComponent,
    CreditCardNewComponent,
    CreditCardNewFieldsComponent,
    BankAccountNewComponent,
    AddressComponent,
    AddressEditComponent,
    AddressNewComponent,
    AddressFieldsComponent,
    PhoneComponent,
    PhoneSearchComponent,
    PhoneNewComponent,
    PhoneEditComponent,
    CRMComponent,
    CRMEditComponent,
    CRMNewComponent,
    CRMTrackingComponent,
    CampaignCategoryComponent,
    CampaignCategoryFormComponent,
    CampaignComponent,
    CampaignNewComponent,
    CampaignEditComponent,
    CampaignPhoneComponent,
    CampaignEmailComponent,
    CampaignFunnelsComponent,
    CampaignStylesComponent,
    CampaignVerifyComponent,
    CampaignMenuComponent,
    CampaignSummaryComponent,
    ProductCategoryComponent,
    ProductCategoryFormComponent,
    HeaderComponent,
    FunnelComponent,
    FunnelEditComponent,
    FunnelNewComponent,
    FunnelStepsComponent,
    FunnelStepEditComponent,
    FunnelStepNewComponent,
    FunnelInputEditComponent,
    FunnelInputNewComponent,
    FunnelActionEditComponent,
    FunnelActionNewComponent,
    StyleComponent,
    StyleNewComponent,
    StyleEditComponent,
    StyleUploadComponent,
    AudioFileComponent,
    AudioFileUploadComponent,
    EmailTemplateComponent,
    EmailTemplateNewComponent,
    EmailTemplateEditComponent,
    AutoResponderComponent,
    AutoResponderNewComponent,
    AutoResponderEditComponent,
    AutoResponderResponseComponent,
    SidebarComponent,
    LegacySidebarComponent,
    RegisterSuccessComponent,
    PasswordChangeComponent,
    PasswordResetComponent,
    PasswordResetSuccessComponent,
    PasswordResetConfirmComponent,
    FaqsComponent,
    FaqNewComponent,
    FaqEditComponent,
    FaqTopicNewComponent,
    FaqTopicEditComponent,
    CampaignTestComponent,
    CampaignProductsComponent,
    TroubleshooterComponent,
    SessionComponent,
    SessionDetailComponent,
    ChatbotSessionDetailComponent,
    SurveyComponent,
    LifelineComponent,
    AccountComponent,
    AccountFormComponent,
    FunnelStepGroupComponent,
    FunnelStepGroupFormComponent,
    CampaignTrackingComponent,
    CampaignTrackingFormComponent,
    CampaignLinksComponent,
    VoiceFunnelComponent,
    AccountEmailBlacklistComponent,
    CRMCampaignComponent,
    ReportsComponent,
    ReportTableComponent,
    CampaignMarketplaceComponent,
    ReportsConfigComponent,
    CampaignCallCenterComponent,
    CallCenterActionComponent,
    CallCenterActionFormComponent,
    CampaignCampaignsComponent,
    HybridFunnelComponent,
    HybridCampaignComponent,
    HybridCampaignNewComponent,
    PhoneLogsContainerComponent,
    SmsLogsContainerComponent,
    PhoneLogsComponent,
    SmsLogsComponent,
    EmailLogsComponent,
    EmailLogsContainerComponent,
    CampaignDomainComponent,
    CampaignDomainFormComponent,
    CampaignDomainPhoneComponent,
    CampaignDomainFunnelsComponent,
    PreviewFormComponent,
    FunnelPreviewFormComponent,
    FunnelStepPreviewFormComponent,
    WebBuilderComponent,
    DesignTemplateComponent,
    TemplateGridComponent,
    TemplateSettingComponent,
    DesignTemplateEditFormComponent,
    WidgetCategoryComponent,
    PlanComponent,
    PlanNewComponent,
    PlanEditComponent,
    ResolvePlanComponent,
    ResolvePlanNewComponent,
    ResolvePlanEditComponent,
    WidgetCategoryFormComponent,
    InvoiceItemComponent,
    InvoiceItemFormComponent,
    InvoiceItemsChargeComponent,
    InvoiceComponent,
    InvoiceDetailComponent,
    InvoiceChargeComponent,
    InvoiceTransferCreditComponent,
    InvoiceSplitComponent,
    InvoiceBillingDateComponent,
    CustomerComponent,
    SessionResultsComponent,
    CRMCopyOrderComponent,
    TermsAndConditionsComponent,
    SipCredentialFormComponent,
    FaqReportsComponent,
    UserComponent,
    UserInviteComponent,
    UserInviteAcceptComponent,
    VisualFunnelComponent,
    ProductFunnelComponent,
    ProfilerEnableComponent,
    ProfilerDisableComponent,
    StylePreviewFormComponent,
    PerformanceReportComponent,
    PersonalInfoComponent,
    AutoresponderTriggersComponent,
    PathPerformanceReportComponent,
    AccountSearchKeywordsComponent,
    NotificationComponent,
    AskSolvpathComponent,
    RefundReportComponent,
    ResendActivationEmailComponent,
    RelatedProductSetCategoryComponent,
    RelatedProductSetCategoryNewComponent,
    RelatedProductSetCategoryEditComponent,
    RelatedProductSetNewComponent,
    RelatedProductSetEditComponent,
    ReasonCategoryComponent,
    ReasonCategoryNewComponent,
    ReasonCategoryEditComponent,
    FolderComponent,
    FolderNewComponent,
    FolderEditComponent,
    ChargebackReportComponent,
    FunnelTreeActionsComponent,
    SessionSummaryReportComponent,
    UserApiKeyComponent,
    UserProxyKeyComponent,
    MerchantComponent,
    MerchantProfilesComponent,
    MerchantProfilesNewComponent,
    MerchantProfilesEditComponent,
    MerchantProfilesResolveComponent,
    ResolveTestOrderComponent,
    MarketplaceReportComponent,
    RetentionReportComponent,
    DashboardComponent,
    GatewayDashboardComponent,
    GatewaySummaryComponent,
    ApprovalAnalyticsComponent,
    GatewayFilterBarComponent,
    OverviewComponent,
    RetentionComponent,
    MarketplaceComponent,
    DashboardSubscriptionComponent,
    DashboardOrdersComponent,
    DashboardResolveComponent,
    DashboardReturnsComponent,
    DashboardPhoneComponent,
    DoughnutChartComponent,
    DataTableComponent,
    ProgressChartComponent,
    LoaderComponent,
    DashboardFilterBarComponent,
    BarChartComponent,
    PerfTableCardComponent,
    PerfTableComponent,
    StatRowCardComponent,
    SummarySectionCardComponent,
    CardLoaderComponent,
    InfoCardComponent,
    DestinationPanelComponent,
    DoughnutSummaryCardComponent,
    BillingDualCardComponent,
    Unlock360BannerComponent,
    ViewToggleComponent,
    ProductActivityCardComponent,
    PortalActionsCardComponent,
    AiInsightsCardComponent,
    CardSelectComponent,
    SplitBarComponent,
    TablePanelComponent,
    PaymentUpdaterReportComponent,
    LineGraphComponent,
    DoughnutLegendTableComponent,
    EmptyStateCardComponent,
    DashboardOverviewReportComponent,
    DashboardResolveReportComponent,
    DashboardRetentionReportComponent,
    DashboardMarketplaceReportComponent,
    DashboardReturnsReportComponent,
    DashboardOrdersReportComponent,
    DashboardSubscriptionReportComponent,
    CustomMultiSelectComponent,
    DevEnvComponent,
    DevEnvNewComponent,
    CrmChargebackConfigComponent,
    SolvpathDocumentationComponent,
    ChargebackReportV2Component,
    SPTouchedChargebackReportV2Component,
    PaymentUpdaterReportV2Component,
    SSOCompleteComponent,
    ReportOverviewPanelV3Component,
    ReportTopIntentsPanelV3Component,
    ReportPortalMomentsPanelV3Component,
    ReportPortalActionsPanelV3Component,
    CustomerSupportReportV3Component,
    CustomerJourneyReportV3Component,
    ResolveReportV3Component,
    ReportTableV2Component,
    PaymentUpdaterReportV2Component,
    ChargebackReportV2Component,
    SPTouchedChargebackReportV2Component,
    RefundReportV2Component,
    ChatbotReportV2Component,
    GatewayTransactionReportV2Component,
    GatewayPerformanceReportV3Component,
    OrderOptionsReportComponent,
    TrafficSourceReportComponent,
    SingleStepReportComponent,
    SubscriptionRetentionReportV2Component,
    SubscriptionRetentionReportV3Component,
    ReturnRetentionReportV2Component,
    NewOrderRetentionReportV2Component,
    OrderRetentionReportV3Component,
    ReturnRetentionReportV3Component,
    TicketSystemComponent,
    TicketSystemEditComponent,
    TicketSystemNewComponent,
    SSOCompleteComponent,
    ShippingSystemComponent,
    ShippingSystemNewComponent,
    ShippingSystemLabelFieldsComponent,
    ShippingSystemEditComponent,
    AudienceComponent,
    AudienceNewComponent,
    AudienceEditComponent,
    AudiencePopupComponent,
    PathComponent,
    PathNewComponent,
    PathEditComponent,
    PathStepComponent,
    PathCrudPopupComponent,
    CustomerPortalEditComponent,
    InvoiceTransactionComponent,
    InvoiceTransactionEditComponent,
    InvoiceTransactionRefundComponent,
    EmailIntegrationSelectComponent,
    EmailIntegrationForwardingNewComponent,
    EmailIntegrationForwardingEditComponent,
    ChatbotQuicklinkComponent,
    GmailAuthCallbackComponent,
    ChatbotInactivityMessageComponent,
    MouseflowToggleComponent,
    CampaignNewPopupComponent,
    OrderManagementPortalComponent,
    OmpHeaderComponent,
    OmpSidebarComponent,
    OmpColorInputComponent,
    OmpLabelInputComponent,
    OmpToggleComponent,
    OmpSliderComponent,
    OmpFaqTreeComponent,
    OmpRteComponent,
    OmpImageFieldComponent,
    OmpWelcomeMessageSettingsComponent,
    OmpAnnouncementBarSettingsComponent,
    OmpTextBoxSettingsComponent,
    OmpSubscriptionListSettingsComponent,
    OmpOrderListSettingsComponent,
    OmpOrderSummarySettingsComponent,
    OmpShippingPaymentSettingsComponent,
      OmpNextOrderDetailsSettingsComponent,
    OmpMomentCardSettingsComponent,
    OmpIconGlyphComponent,
    OmpIconFieldComponent,
    OmpIconPickerComponent,
    OmpCrossSellCarouselSettingsComponent,
    OmpImageBannerSettingsComponent,
    OmpFaqSettingsComponent,
    OmpFooterSettingsComponent,
    OmpInstagramSettingsComponent,
    OmpNavBlocksSettingsComponent,
    OmpCtaFieldsComponent,
    OmpTextAndImageSettingsComponent,
    OmpCalloutSettingsComponent,
    OmpVideoSettingsComponent,
    OmpColumnsSettingsComponent,
    CampaignSidebarComponent,
    CampaignGeneralComponent,
    CampaignGeneralEditPopupComponent,
    CampaignBrandingComponent,
    CampaignDomainsComponent,
    CampaignCallCenterSettingsComponent,
    PhoneContactFieldsComponent,
    CampaignPhoneSetupComponent,
    CampaignEmailSetupComponent,
    PhoneSetupPopupComponent,
    CampaignAutorespondersComponent,
    ContactFieldPopupComponent,
    AutoresponderComponent,
    EmailTemplatePopupComponent,
    CampaignCustomerLoginSettingsComponent,
    CampaignPolicyComponent,
    CampaignDeploymentPageComponent,
    CampaignCustomerPortalSettingsComponent,
    CampaignMarketplaceSettingsComponent,
    CampaignCRMsComponent,
    SmartlinkPreviewComponent,
    SmartlinkPreviewFormComponent,
  ],
  providers: [
    OmpPickerService,
    AuthGuard,
    AnonymousGuard,
    CampaignGuard,
    StorageService,
    ColorSwatchService,
    EditorColorService,
    ClipboardService,
    RegionService,
    AuthenticationService,
    UserService,
    PaymentSourceService,
    AddressService,
    PhoneService,
    CRMService,
    CampaignCategoryService,
    CampaignService,
    ProductCategoryService,
    ProductService,
    FunnelService,
    FunnelStepService,
    FunnelInputService,
    StyleService,
    FunnelActionService,
    AudioFileService,
    EmailTemplateService,
    AutoResponderService,
    CampaignProductService,
    CRMCampaignService,
    FaqService,
    FaqTopicService,
    OrderService,
    IconFileService,
    ProductInfoSectionService,
    SessionService,
    AccountService,
    FunnelStepGroupService,
    CampaignTrackingService,
    WidgetService,
    SalesPhraseService,
    CallCenterActionService,
    TinymceConfig,
    CanDeactivateGuard,
    CanDeactivateService,
    GuiService,
    SideNavService,
    CallLogsService,
    SmsLogsService,
    MailLogsService,
    ReportsConfigService,
    ReportsService,
    CampaignDomainService,
    WidgetCategoryService,
    PlanService,
    ResolvePlanService,
    InvoiceService,
    InvoiceItemService,
    InvoiceTransactionService,
    DailyInvoiceService,
    CustomerService,
    SipCredentialService,
    VariantService,
    FaqReportsService,
    CycleService,
    PusherService,
    HeartbeatService,
    PerformanceReportService,
    PersonalInfoService,
    PathPerformanceReportService,
    NotificationService,
    RefundReportService,
    ChatbotReportService,
    GatewayTransactionReportService,
    GatewayPerformanceReportService,
    CustomerSupportReportService,
    CustomerJourneyReportService,
    ResolveReportService,
    GatewayDashboardService,
    ApprovalAnalyticsDashboardService,
    OrderOptionsReportService,
    SingleStepReportService,
    ExchangeableProductsSetService,
    RelatedProductSetCategoryService,
    RelatedProductSetService,
    FolderService,
    FulfillmentService,
    ThemeService,
    ChargebackReportService,
    SessionSummaryReportService,
    ApiKeyService,
    ProxyKeyService,
    MerchantService,
    MarketplaceReportService,
    RetentionReportService,
    OrderRetentionReportService,
    SubscriptionRetentionReportService,
    ReturnRetentionReportService,
    PhoneCallService,
    CheckoutsService,
    CheckoutTemplatesService,
    PaymentUpdaterReportService,
    DevEnvService,
    TicketSystemService,
    ShippingSystemService,
    AudienceService,
    PathService,
    CancelReasonCategoryService,
    AudienceService,
    PathService,
    ResolveSettingsService,
    ResolveMerchantService,
    SingleStepFilterService,
    EmailIntegrationService,
    CheckoutBankService,
    CheckoutGatewayService,
    CheckoutProcessorService,
    MerchantCategoryService,
    MerchantProfileService,
    ProcessorDocumentService,
    DeploymentLinkService,
    GmailService,
    MouseflowService,
    IntercomService,
    AskSolvpathService,
    {
      provide: ErrorHandler,
      useClass: SentryErrorHandler
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptor,
      multi: true
    },
    {
      provide: RECAPTCHA_SETTINGS,
      useValue: { siteKey: environment.captchaKey } as RecaptchaSettings
    }
  ],
  bootstrap: [AppComponent]
})

export class AppModule {
  constructor(
    private tinymce: TinymceConfig
  ) {
    this.tinymce.setDefaults(); // set defaults for tinymce editors
    this.tinymce.createPlugins(); // create custom plugins
  }
}
